// frontend/src/pages/Wallet.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { walletAPI, withdrawalAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../context/FeaturesContext';
import {
  Card, Button, Badge, Modal, Input, Select,
  Spinner, Empty, Table, Td, SectionHeader,
} from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { RefreshCw, ArrowDownCircle, ArrowUpCircle, Send, Download, Copy, ArrowLeftRight } from 'lucide-react';
import { format } from 'date-fns';

// ── Deposit method configs ──────────────────────────────────────
const DEPOSIT_METHODS = [
  { id: 'mtn_momo',     label: 'MTN Mobile Money', icon: '📱', color: '#F59E0B' },
  { id: 'airtel_money', label: 'Airtel Money',      icon: '📲', color: '#EF4444' },
  { id: 'crypto_usdt',  label: 'USDT (Crypto)',     icon: '₿',  color: '#10B981' },
  { id: 'agent_cash',   label: 'Agent Cash',        icon: '💵', color: '#8B5CF6' },
];

const WITHDRAW_METHODS = [
  { id: 'mtn_momo',     label: 'MTN Mobile Money', icon: '📱' },
  { id: 'airtel_money', label: 'Airtel Money',      icon: '📲' },
];

const WD_CURRENCIES = [
  { id: 'UGX',  flag: '🇺🇬', label: 'UGX — Ugandan Shilling', min: 1000, max: 5000000,  kycThreshold: 500000  },
  { id: 'USD',  flag: '🇺🇸', label: 'USD — US Dollar',         min: 1,    max: 10000,    kycThreshold: 135     },
  { id: 'EUR',  flag: '🇪🇺', label: 'EUR — Euro',              min: null, max: null,     kycThreshold: null    },
  { id: 'USDT', flag: '₮',   label: 'USDT — Tether TRC-20',   min: null, max: null,     kycThreshold: null    },
];

// Deposit currencies
const DEP_CURRENCIES = [
  { id: 'UGX',  flag: '🇺🇬', label: 'UGX',  name: 'Ugandan Shilling' },
  { id: 'USD',  flag: '🇺🇸', label: 'USD',  name: 'US Dollar' },
  { id: 'EUR',  flag: '🇪🇺', label: 'EUR',  name: 'Euro' },
  { id: 'USDT', flag: '₮',   label: 'USDT', name: 'Tether (TRC-20)' },
];

const TX_COLOR = {
  deposit: 'green', withdrawal: 'red', transfer_in: 'blue', transfer_out: 'amber',
  investment_in: 'purple', investment_return: 'cyan', commission: 'gold',
  revenue_share: 'gold', savings_lock: 'blue', savings_unlock: 'cyan',
  subscription_payment: 'amber', refund: 'green', fee: 'red',
  fx_conversion: 'cyan', savings_deposit: 'blue', savings_withdrawal: 'amber',
};
const TX_SIGN = {
  deposit: '+', transfer_in: '+', investment_return: '+',
  commission: '+', revenue_share: '+', savings_unlock: '+', refund: '+', savings_withdrawal: '+',
};

// ── Formatters ──────────────────────────────────────────────────
const fmtUGX  = (n) => `UGX ${Math.round(n || 0).toLocaleString()}`;
const fmtUSD  = (n) => `$${(n || 0).toFixed(2)}`;
const fmtEUR  = (n) => `€${(n || 0).toFixed(2)}`;
const fmtUSDT = (n) => `${(n || 0).toFixed(2)} USDT`;

// ── CSV download ────────────────────────────────────────────────
function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map(row => headers.map(h => {
      const val = row[h] ?? '';
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
    }).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Statement Tab ───────────────────────────────────────────────
function StatementTab({ fetchAllTransactions }) {
  const [allTx, setAllTx]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded]   = useState(false);
  const [period, setPeriod]   = useState('30');

  const load = async () => {
    setLoading(true);
    try {
      const rows = await fetchAllTransactions();
      setAllTx(rows);
      setLoaded(true);
    } catch { toast.error('Failed to load statement'); }
    finally { setLoading(false); }
  };

  const filtered = allTx.filter(tx => {
    const days = parseInt(period, 10);
    if (!days) return true;
    return new Date(tx.createdAt) >= new Date(Date.now() - days * 86400000);
  });

  const totals = filtered.reduce((acc, tx) => {
    const sign = TX_SIGN[tx.type] ? 1 : -1;
    acc.net += (tx.amount || 0) * sign;
    if (sign === 1) acc.credits += tx.amount || 0;
    else            acc.debits  += tx.amount || 0;
    return acc;
  }, { net: 0, credits: 0, debits: 0 });

  return (
    <Card>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-text-primary font-bold">Account Statement</h3>
          <p className="text-text-muted text-xs mt-0.5">Download or view your transaction statement</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period} onChange={e => setPeriod(e.target.value)}
            className="bg-bg-panel border border-border rounded-xl px-3 py-2 text-text-primary text-sm outline-none focus:border-cyan"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="0">All time</option>
          </select>
          {!loaded
            ? <Button size="sm" onClick={load} loading={loading}>Load Statement</Button>
            : <Button size="sm" variant="success" onClick={() => {
                if (!filtered.length) { toast.error('No transactions to export'); return; }
                downloadCSV(filtered.map(tx => ({
                  Date: tx.createdAt ? format(new Date(tx.createdAt), 'yyyy-MM-dd HH:mm') : '',
                  Type: tx.type || '', Amount: tx.amount || 0, Currency: tx.currency || 'USD',
                  Method: tx.paymentMethod || 'internal', Status: tx.status || '',
                  Description: (tx.description || '').replace(/,/g, ';'), Reference: tx._id || '',
                })), `DWI_Statement_${period}days_${format(new Date(), 'yyyyMMdd')}.csv`);
                toast.success('Statement downloaded');
              }}>
                <Download className="w-4 h-4" /> Download CSV
              </Button>
          }
        </div>
      </div>

      {loaded && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Transactions', value: filtered.length, color: '#06C8E8' },
              { label: 'Total Credits', value: `+$${totals.credits.toFixed(2)}`, color: '#10B981' },
              { label: 'Total Debits',  value: `-$${totals.debits.toFixed(2)}`,  color: '#EF4444' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-bg-panel border border-border rounded-xl p-3">
                <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">{label}</p>
                <p className="text-lg font-black" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>
          {filtered.length === 0 ? (
            <Empty icon="📄" title="No transactions in this period" />
          ) : (
            <Table headers={['Date', 'Type', 'Amount', 'Currency', 'Status']}>
              {filtered.slice(0, 50).map(tx => {
                const sign = TX_SIGN[tx.type] ? '+' : '-';
                return (
                  <tr key={tx._id}>
                    <Td className="text-xs">{tx.createdAt ? format(new Date(tx.createdAt), 'dd MMM yy HH:mm') : '—'}</Td>
                    <Td className="text-xs capitalize">{tx.type?.replace(/_/g, ' ')}</Td>
                    <Td className={clsx('font-bold text-sm', sign === '+' ? 'text-green' : 'text-red')}>
                      {sign}{tx.currency === 'UGX' ? fmtUGX(tx.amount) : `$${tx.amount?.toFixed(2)}`}
                    </Td>
                    <Td className="text-xs">{tx.currency || 'USD'}</Td>
                    <Td><Badge label={tx.status} color={tx.status === 'completed' ? 'green' : tx.status === 'pending' ? 'amber' : 'red'} /></Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </>
      )}

      {!loaded && !loading && (
        <div className="flex flex-col items-center py-12 gap-3">
          <Download className="w-12 h-12 text-text-muted" />
          <p className="text-text-secondary text-sm">Click "Load Statement" to view and download your transaction history.</p>
        </div>
      )}
    </Card>
  );
}

// ── Main Wallet Component ───────────────────────────────────────
export default function Wallet() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const investEnabled = !(features && features.investment_projects && features.investment_projects.isEnabled === false);
  const [wallet, setWallet]         = useState(null);
  const [rates, setRates]           = useState(null);
  const [transactions, setTx]       = useState([]);
  const [txPagination, setTxPag]    = useState({ total: 0, pages: 1 });
  const [walletLoading, setWalLoad] = useState(true);
  const [txLoading, setTxLoad]      = useState(true);
  const [tab, setTab]               = useState('overview');
  const [txPage, setTxPage]         = useState(1);
  const [txFilter, setTxFilter]     = useState('all');

  // Modals
  const [depositModal, setDepositModal]   = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);

  // Deposit form
  const [depCurrency, setDepCurrency] = useState('UGX');
  const [depMethod, setDepMethod]     = useState('mtn_momo');
  const [depAmount, setDepAmount]     = useState('');
  const [depPhone, setDepPhone]       = useState('');
  const [depLoading, setDepLoading]   = useState(false);

  // Withdraw form (multi-currency)
  const [wdCurrency, setWdCurrency]       = useState('UGX');
  const [wdAmount, setWdAmount]           = useState('');
  const [wdMethod, setWdMethod]           = useState('mtn_momo');
  const [wdPhone, setWdPhone]             = useState('');
  const [wdLoading, setWdLoading]         = useState(false);
  const [wdHistory, setWdHistory]         = useState([]);
  const [wdHistoryLoad, setWdHistoryLoad] = useState(false);
  const [wdStep, setWdStep]              = useState(1); // 1=choose wallet  2=enter details

  // Withdrawal review / confirmation step (display only — no money-movement change)
  const [wdConfirmOpen, setWdConfirmOpen] = useState(false);
  const [wdConfirmed, setWdConfirmed]     = useState(false);
  const [wdQuote, setWdQuote]             = useState(null); // { gross, fee, net } from backend quote endpoint
  const [wdQuoteLoading, setWdQuoteLoading] = useState(false);
  const wdQuoteTimer = useRef(null);

  // Transfer form
  const [tfRecipient, setTfRecipient] = useState('');
  const [tfAmount, setTfAmount]       = useState('');
  const [tfNote, setTfNote]           = useState('');
  const [tfCurrency, setTfCurrency]   = useState('UGX');
  const [tfLoading, setTfLoading]     = useState(false);

  // Exchange modal
  const [exchangeModal, setExchangeModal]   = useState(false);
  const [exFrom, setExFrom]                 = useState('UGX');
  const [exTo, setExTo]                     = useState('USD');
  const [exAmount, setExAmount]             = useState('');
  const [exLoading, setExLoading]           = useState(false);
  const [exPreview, setExPreview]           = useState(null); // { amountOut, rate, spread }

  const fetchWallet = useCallback(async () => {
    setWalLoad(true);
    try {
      const res = await walletAPI.getBalance();
      setWallet(res.wallet);
    } catch { toast.error('Failed to load wallet balance'); }
    finally { setWalLoad(false); }
  }, []);

  const fetchRates = useCallback(async () => {
    try {
      const res = await walletAPI.getRates();
      setRates(res.rates);
    } catch { /* rates unavailable — use fallback */ }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setTxLoad(true);
    try {
      const params = { page: txPage, limit: 15 };
      if (txFilter !== 'all') params.type = txFilter;
      const res = await walletAPI.getTransactions(params);
      setTx(res.transactions || []);
      setTxPag(res.pagination || { total: 0, pages: 1 });
    } catch { toast.error('Failed to load transactions'); }
    finally { setTxLoad(false); }
  }, [txPage, txFilter]);

  // Must be declared before the useEffect that lists it as a dependency.
  // Declaring it after caused a TDZ ReferenceError that crashed the component.
  const fetchWdHistory = useCallback(async () => {
    setWdHistoryLoad(true);
    try {
      const res = await withdrawalAPI.getHistory();
      setWdHistory(res?.withdrawals || []);
    } catch { /* silently ignore — feature may be disabled */ }
    finally  { setWdHistoryLoad(false); }
  }, []);

  useEffect(() => { fetchWallet(); fetchRates(); fetchWdHistory(); }, [fetchWallet, fetchRates, fetchWdHistory]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  // ── Deposit estimated credit display ───────────────────────
  const getEstimatedCredit = () => {
    const amt = parseFloat(depAmount);
    if (!amt || isNaN(amt)) return null;
    const USD_TO_UGX = rates?.USD_TO_UGX || 3700;
    const EUR_TO_USD = rates?.EUR_TO_USD || 1.08;
    if (depCurrency === 'UGX')  return { wallet: 'UGX Wallet',  credit: fmtUGX(amt) };
    if (depCurrency === 'USD')  return { wallet: 'USD Wallet',  credit: fmtUSD(amt), ugxNote: `≈ ${fmtUGX(amt * USD_TO_UGX)} collected` };
    if (depCurrency === 'EUR')  return { wallet: 'EUR Wallet',  credit: fmtEUR(amt), ugxNote: `≈ ${fmtUGX(amt * EUR_TO_USD * USD_TO_UGX)} collected` };
    if (depCurrency === 'USDT') return { wallet: 'USDT Wallet', credit: fmtUSDT(amt) };
    return null;
  };

  // ── Min deposit labels ────────────────────────────────────
  const DEP_MIN = { UGX: 500, USD: 0.14, EUR: 0.13, USDT: 1 };
  const DEP_PLACEHOLDER = { UGX: 'e.g. 5,000', USD: 'e.g. 1.00', EUR: 'e.g. 0.50', USDT: 'e.g. 1.00' };

  const handleDeposit = async () => {
    const amt = parseFloat(depAmount);
    if (!amt || amt < DEP_MIN[depCurrency]) {
      toast.error(`Minimum deposit: ${DEP_MIN[depCurrency]} ${depCurrency}`);
      return;
    }
    const needsPhone = ['mtn_momo', 'airtel_money'].includes(depMethod);
    if (needsPhone && !depPhone) { toast.error('Enter your phone number'); return; }

    setDepLoading(true);
    try {
      await walletAPI.deposit({
        amount: amt,
        currency: depCurrency,
        method: depMethod,
        phone: depPhone || undefined,
      });
      toast.success(
        depMethod === 'agent_cash' ? 'Visit a DWI agent and show your reference number.' :
        depMethod === 'crypto_usdt' ? 'Send USDT to the address shown — credited after confirmation.' :
        'USSD prompt sent — approve on your phone!'
      );
      setDepositModal(false);
      setDepAmount(''); setDepPhone('');
      fetchWallet(); fetchTransactions();
    } catch (err) {
      toast.error(err.message || 'Deposit failed');
    } finally {
      setDepLoading(false);
    }
  };

  const getWdBalance = () => {
    const w2 = wallet || {};
    if (wdCurrency === 'UGX')  return w2.ugxBalance  || 0;
    if (wdCurrency === 'USD')  return w2.balance      || 0;
    if (wdCurrency === 'EUR')  return w2.eurBalance   || 0;
    if (wdCurrency === 'USDT') return w2.usdtBalance  || 0;
    return 0;
  };

  const fmtWdBalance = (n) => {
    if (wdCurrency === 'UGX')  return fmtUGX(n);
    if (wdCurrency === 'USD')  return fmtUSD(n);
    if (wdCurrency === 'EUR')  return fmtEUR(n);
    if (wdCurrency === 'USDT') return `${(n || 0).toFixed(2)} USDT`;
    return String(n);
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(wdAmount);

    if (!amt || isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (wdCurrConf.min && amt < wdCurrConf.min) {
      toast.error(`Minimum withdrawal: ${wdCurrConf.min.toLocaleString()} ${wdCurrency}`);
      return;
    }
    if (!wdPhone) { toast.error('Enter recipient phone number'); return; }
    if (amt > wdAvailable) {
      toast.error(`Insufficient ${wdCurrency} balance. Available: ${fmtWdBalance(wdAvailable)}`);
      return;
    }
    if (wdKycThr && amt >= wdKycThr && !kycApproved) {
      toast.error(`KYC verification required for ${wdCurrency} withdrawals above ${wdKycThr.toLocaleString()}`);
      return;
    }

    setWdLoading(true);
    try {
      if (wdCurrency === 'UGX') {
        await withdrawalAPI.initiate({ amount: amt, method: wdMethod, phone: wdPhone, currency: 'UGX' });
        toast.success('Withdrawal request submitted! Pending admin approval.');
      } else {
        await walletAPI.withdraw({ amount: amt, method: wdMethod, phone: wdPhone, currency: wdCurrency });
        toast.success('Withdrawal request submitted!');
      }
      setWdConfirmOpen(false); setWdConfirmed(false); setWdQuote(null);
      setWithdrawModal(false);
      setWdAmount(''); setWdPhone(''); setWdStep(1);
      fetchWallet(); fetchTransactions(); fetchWdHistory();
    } catch (err) {
      setWdConfirmOpen(false);
      toast.error(err.message || 'Withdrawal failed');
    } finally {
      setWdLoading(false);
    }
  };

  const openWithdrawFor = (currency) => {
    setWdCurrency(currency);
    setWdStep(2);
    setWdAmount('');
    setWdPhone('');
    setWithdrawModal(true);
  };

  // Fetch a fee quote from the backend (no funds moved). Normalises the two
  // quote endpoints (UGX withdrawals vs legacy USD wallet withdraw) to
  // { gross, fee, net, breakdown }. `breakdown` (Service fee + Network cost
  // line items) is only populated by the backend when WITHDRAWAL_FEE_ENGINE_V2
  // is on — flag off, it stays null and the UI below renders exactly as before.
  const fetchWdQuote = useCallback(async (amt) => {
    if (wdCurrency === 'UGX') {
      const res = await withdrawalAPI.quote({ amount: amt, currency: 'UGX' });
      return {
        gross: res.amount, fee: res.fee, net: res.netAmount,
        breakdown: res.dwiFee != null ? res.breakdown : null,
      };
    }
    const res = await walletAPI.withdrawQuote({ amount: amt, currency: wdCurrency });
    return { gross: res.amountUSD ?? amt, fee: res.feeUSD, net: res.netAmountUSD, breakdown: null };
  }, [wdCurrency]);

  // Debounced live quote for the step-2 fee summary box (replaces the old hardcoded 1%).
  useEffect(() => {
    if (!withdrawModal || wdStep !== 2) return undefined;
    const amt = parseFloat(wdAmount);
    if (!amt || isNaN(amt) || amt <= 0 || !(wdCurrency === 'UGX' || wdCurrency === 'USD')) {
      setWdQuote(null);
      return undefined;
    }
    let cancelled = false;
    setWdQuoteLoading(true);
    clearTimeout(wdQuoteTimer.current);
    wdQuoteTimer.current = setTimeout(async () => {
      try {
        const q = await fetchWdQuote(amt);
        if (!cancelled) setWdQuote(q);
      } catch {
        if (!cancelled) setWdQuote(null);
      } finally {
        if (!cancelled) setWdQuoteLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(wdQuoteTimer.current); };
  }, [wdAmount, wdCurrency, wdStep, withdrawModal, fetchWdQuote]);

  // Step-2 "Review Withdrawal" — validate, get an authoritative quote, open the confirm modal.
  const handleWithdrawReview = async () => {
    const amt = parseFloat(wdAmount);
    if (!amt || isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (wdCurrConf.min && amt < wdCurrConf.min) {
      toast.error(`Minimum withdrawal: ${wdCurrConf.min.toLocaleString()} ${wdCurrency}`);
      return;
    }
    if (!wdPhone) { toast.error('Enter recipient phone number'); return; }
    if (amt > wdAvailable) {
      toast.error(`Insufficient ${wdCurrency} balance. Available: ${fmtWdBalance(wdAvailable)}`);
      return;
    }
    if (wdKycThr && amt >= wdKycThr && !kycApproved) {
      toast.error(`KYC verification required for ${wdCurrency} withdrawals above ${wdKycThr.toLocaleString()}`);
      return;
    }
    setWdQuoteLoading(true);
    try {
      const q = await fetchWdQuote(amt);
      setWdQuote(q);
      setWdConfirmed(false);
      setWdConfirmOpen(true);
    } catch (err) {
      toast.error(err.message || 'Could not calculate the withdrawal fee. Please try again.');
    } finally {
      setWdQuoteLoading(false);
    }
  };

  // Compute exchange preview from wallet rates (no server round-trip needed for preview)
  const computeExPreview = (from, to, amt) => {
    const a = parseFloat(amt);
    if (!a || a <= 0) { setExPreview(null); return; }
    const USD_TO_UGX = rates?.USD_TO_UGX || 3700;
    const EUR_TO_USD = rates?.EUR_TO_USD || 1.08;
    const SPREAD = 0.005;
    const toUSDv = (v, c) => c === 'UGX' ? v / USD_TO_UGX : c === 'EUR' ? v * EUR_TO_USD : v;
    const fromUSDv = (v, c) => c === 'UGX' ? v * USD_TO_UGX : c === 'EUR' ? v / EUR_TO_USD : v;
    const inUSD = toUSDv(a, from);
    const out = fromUSDv(inUSD * (1 - SPREAD), to);
    const displayOut = to === 'UGX' ? Math.round(out) : parseFloat(out.toFixed(6));
    setExPreview({ amountOut: displayOut, spread: SPREAD, feeUSD: inUSD * SPREAD });
  };

  const handleExchange = async () => {
    const amt = parseFloat(exAmount);
    if (!amt || exFrom === exTo) { toast.error('Invalid exchange parameters'); return; }
    setExLoading(true);
    try {
      const res = await walletAPI.exchange({ fromCurrency: exFrom, toCurrency: exTo, amount: amt });
      toast.success(res.message || 'Exchange completed!');
      setExchangeModal(false);
      setExAmount(''); setExPreview(null);
      fetchWallet(); fetchTransactions();
    } catch (err) {
      toast.error(err.message || 'Exchange failed');
    } finally {
      setExLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!tfRecipient) { toast.error('Enter recipient email or phone'); return; }
    const amt = parseFloat(tfAmount);
    const tfAvail = tfCurrency === 'UGX' ? (w.ugxBalance || 0)
      : tfCurrency === 'USD'  ? (w.balance     || 0)
      : tfCurrency === 'EUR'  ? (w.eurBalance  || 0)
      : (w.usdtBalance || 0);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > tfAvail) {
      const bal = tfCurrency === 'UGX' ? fmtUGX(tfAvail) : tfCurrency === 'EUR' ? fmtEUR(tfAvail) : tfCurrency === 'USDT' ? fmtUSDT(tfAvail) : fmtUSD(tfAvail);
      toast.error(`Insufficient ${tfCurrency} balance. Available: ${bal}`);
      return;
    }
    if (!amt || amt < 0.01) { toast.error('Enter a valid amount'); return; }
    setTfLoading(true);
    try {
      const res = await walletAPI.transfer({ recipient: tfRecipient, amount: amt, currency: tfCurrency, note: tfNote || undefined });
      toast.success(res.message || 'Transfer completed!');
      setTransferModal(false);
      setTfRecipient(''); setTfAmount(''); setTfNote('');
      fetchWallet(); fetchTransactions();
    } catch (err) {
      toast.error(err.message || 'Transfer failed');
    } finally {
      setTfLoading(false);
    }
  };

  const w = wallet || { balance: 0, ugxBalance: 0, eurBalance: 0, usdtBalance: 0, savingsBalance: 0, commissionBalance: 0, investmentBalance: 0 };

  // ── Wallet cards in display order ───────────────────────────
  // wdId = WD_CURRENCIES key — set only for wallets that support direct withdrawal
  const WALLET_CARDS = [
    { key: 'ugx',        icon: '🇺🇬', label: 'UGX Wallet',        sub: 'Primary · Ugandan Shilling', value: fmtUGX(w.ugxBalance),        color: '#FFD700', raw: w.ugxBalance,        wdId: 'UGX'  },
    { key: 'usd',        icon: '🇺🇸', label: 'USD Wallet',        sub: 'US Dollar',                  value: fmtUSD(w.balance),            color: '#06C8E8', raw: w.balance,            wdId: 'USD'  },
    { key: 'eur',        icon: '🇪🇺', label: 'EUR Wallet',        sub: 'Euro',                       value: fmtEUR(w.eurBalance),         color: '#3B82F6', raw: w.eurBalance,         wdId: 'EUR'  },
    { key: 'usdt',       icon: '₮',   label: 'USDT Wallet',       sub: 'Tether · TRC-20',            value: fmtUSDT(w.usdtBalance),       color: '#26A17B', raw: w.usdtBalance,        wdId: 'USDT' },
    { key: 'savings',    icon: '💰',  label: 'Savings Wallet',    sub: 'Earning interest',           value: fmtUSD(w.savingsBalance),     color: '#8B5CF6', raw: w.savingsBalance },
    { key: 'investment', icon: '📈',  label: 'Investment',        sub: 'Capital in funds',           value: fmtUSD(w.investmentBalance),  color: '#10B981', raw: w.investmentBalance },
    { key: 'commission', icon: '💸',  label: 'Commission Wallet', sub: 'Referral & ISP income',      value: fmtUSD(w.commissionBalance),  color: '#EAB308', raw: w.commissionBalance },
  ];

  const est = getEstimatedCredit();
  const kycApproved = user?.kyc?.status === 'approved';
  const wdAmt = parseFloat(wdAmount) || 0;

  // Withdrawal modal computed values (hoisted from IIFE for render safety)
  const wdCurrConf    = WD_CURRENCIES.find(c => c.id === wdCurrency) || WD_CURRENCIES[0];
  const wdAvailable   = getWdBalance();
  const wdIsMoMo      = wdCurrency === 'UGX' || wdCurrency === 'USD';
  const wdKycThr      = wdCurrConf.kycThreshold;
  const wdShowKyc     = !!(wdKycThr && wdAmt >= wdKycThr && !kycApproved);
  const wdMinAmt      = wdCurrConf.min || 1;

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">DWIPay Wallet</h1>
          <p className="text-text-secondary text-sm mt-1">Deposits, withdrawals, transfers and transaction history.</p>
        </div>
        <button
          onClick={() => { fetchWallet(); fetchTransactions(); }}
          className="text-text-muted hover:text-cyan transition-colors p-2 rounded-xl hover:bg-cyan/10"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-2">
        <Button onClick={() => setDepositModal(true)} className="flex-col gap-1.5 py-3 h-auto text-xs" size="sm">
          <ArrowDownCircle className="w-5 h-5" />
          <span>Deposit</span>
        </Button>
        <Button variant="outline" onClick={() => { setWdStep(1); setWithdrawModal(true); }} className="flex-col gap-1.5 py-3 h-auto text-xs" size="sm">
          <ArrowUpCircle className="w-5 h-5" />
          <span>Withdraw</span>
        </Button>
        <Button variant="secondary" onClick={() => setTransferModal(true)} className="flex-col gap-1.5 py-3 h-auto text-xs" size="sm">
          <Send className="w-5 h-5" />
          <span>Send</span>
        </Button>
        <Button variant="ghost" onClick={() => setExchangeModal(true)} className="flex-col gap-1.5 py-3 h-auto text-xs border border-border" size="sm">
          <ArrowLeftRight className="w-5 h-5" />
          <span>Exchange</span>
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[['overview', 'Overview'], ['transactions', 'Transactions'], ['withdrawals', 'Withdrawals'], ['statement', 'Statement'], ['exchange', 'Exchange']].map(([id, label]) => (
          <button
            key={id} onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}
          >{label}</button>
        ))}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────── */}
      {tab === 'overview' && (
        <Card>
          <SectionHeader title="Your Wallets" />
          {walletLoading ? (
            <div className="flex justify-center py-8"><Spinner size="lg" /></div>
          ) : (
            <div className="space-y-2">
              {WALLET_CARDS.filter(c => c.key !== 'investment' || investEnabled).map((card) => (
                <div key={card.key} className="flex items-center gap-3 p-3 rounded-xl bg-bg-panel border border-border">
                  <span className="text-xl w-7 text-center flex-shrink-0">{card.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary text-sm font-semibold">{card.label}</p>
                    <p className="text-text-muted text-xs">{card.sub}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <p className="font-black text-base whitespace-nowrap" style={{ color: card.raw > 0 ? card.color : undefined }}>
                      {card.value}
                    </p>
                    {card.wdId && (
                      <button
                        onClick={() => openWithdrawFor(card.wdId)}
                        className="text-[10px] font-bold text-cyan border border-cyan/40 bg-cyan/10 hover:bg-cyan/20 rounded-lg px-2 py-1 transition-all"
                      >
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Exchange rate info */}
          {rates && (
            <div className="mt-4 bg-bg-panel border border-border rounded-xl p-3">
              <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2">Exchange Rates</p>
              <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
                <span>1 USD = {rates.USD_TO_UGX.toLocaleString()} UGX</span>
                <span>·</span>
                <span>1 EUR = {rates.EUR_TO_UGX.toLocaleString()} UGX</span>
                <span>·</span>
                <span>1 EUR ≈ ${rates.EUR_TO_USD.toFixed(2)} USD</span>
              </div>
            </div>
          )}

          {/* Limits */}
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="bg-green/10 border border-green/25 rounded-xl p-3">
              <p className="text-green font-bold text-xs mb-1">✓ Deposit</p>
              <p className="text-text-secondary text-xs">No KYC required. All amounts welcome.</p>
              <p className="text-text-muted text-xs">Min: UGX 500 · Max: UGX 50,000,000/day</p>
            </div>
            <div className="bg-amber/10 border border-amber/25 rounded-xl p-3">
              <p className="text-amber font-bold text-xs mb-1">⚠ Withdrawal</p>
              <p className="text-text-secondary text-xs">KYC required for withdrawals above UGX 500,000.</p>
              <p className="text-text-muted text-xs">Fee shown at review · Min UGX 1,000 · Reviewed within 1–24 hours</p>
            </div>
          </div>
        </Card>
      )}

      {/* ── TRANSACTIONS ─────────────────────────────────────── */}
      {tab === 'transactions' && (
        <Card>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-text-primary font-bold text-sm">Transaction History</h3>
            <div className="flex gap-2 flex-wrap">
              {['all', 'deposit', 'withdrawal', 'transfer_in', 'transfer_out', 'commission'].map(f => (
                <button
                  key={f} onClick={() => { setTxFilter(f); setTxPage(1); }}
                  className={clsx(
                    'px-3 py-1 rounded-lg text-[11px] font-semibold capitalize transition-all',
                    txFilter === f ? 'bg-cyan/20 text-cyan border border-cyan/40' : 'bg-bg-panel text-text-muted border border-border hover:border-border-hi'
                  )}
                >{f.replace(/_/g, ' ')}</button>
              ))}
            </div>
          </div>

          {txLoading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : transactions.length === 0 ? (
            <Empty icon="📄" title="No transactions yet" description="Your transaction history will appear here after your first deposit." />
          ) : (
            <>
              <Table headers={['Type', 'Amount', 'Currency', 'Fee', 'Status', 'Date']}>
                {transactions.map(tx => {
                  const sign = TX_SIGN[tx.type] || '-';
                  const fmtAmt = (v, cur) => cur === 'UGX' ? fmtUGX(v) : cur === 'EUR' ? fmtEUR(v) : cur === 'USDT' ? fmtUSDT(v) : fmtUSD(v);
                  return (
                    <tr key={tx._id}>
                      <Td>
                        <div>
                          <Badge label={tx.type.replace(/_/g, ' ')} color={TX_COLOR[tx.type] || 'gray'} />
                          {tx.reference && <p className="text-[9px] font-mono text-text-muted mt-0.5">{tx.reference}</p>}
                        </div>
                      </Td>
                      <Td className={clsx('font-bold', sign === '+' ? 'text-green' : 'text-red')}>
                        {sign}{fmtAmt(tx.amount, tx.currency)}
                      </Td>
                      <Td className="text-xs">{tx.currency || 'USD'}</Td>
                      <Td className="text-xs text-text-muted">
                        {tx.fee > 0 ? fmtAmt(tx.fee, tx.currency) : '—'}
                      </Td>
                      <Td>
                        <Badge label={tx.status} color={tx.status === 'completed' ? 'green' : tx.status === 'pending' || tx.status === 'processing' ? 'amber' : 'red'} />
                      </Td>
                      <Td className="text-xs text-text-muted">
                        {tx.createdAt ? format(new Date(tx.createdAt), 'dd MMM yy HH:mm') : '—'}
                      </Td>
                    </tr>
                  );
                })}
              </Table>
              {txPagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-text-muted text-xs">{txPagination.total} total</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" disabled={txPage === 1} onClick={() => setTxPage(p => p - 1)}>Previous</Button>
                    <span className="text-text-muted text-xs self-center">Page {txPage} of {txPagination.pages}</span>
                    <Button variant="ghost" size="sm" disabled={txPage >= txPagination.pages} onClick={() => setTxPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* ── WITHDRAWALS ──────────────────────────────────────── */}
      {tab === 'withdrawals' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-text-primary font-bold text-sm">Withdrawal History</h3>
            <button onClick={fetchWdHistory} className="text-text-muted hover:text-cyan p-1">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {wdHistoryLoad ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : wdHistory.length === 0 ? (
            <Empty icon="💸" title="No withdrawals yet" description="Your UGX withdrawal requests will appear here." />
          ) : (
            <Table headers={['Ref', 'Amount', 'Net', 'Method', 'Phone', 'Status', 'Date']}>
              {wdHistory.map(w => {
                const statusColor = {
                  pending_approval: 'yellow', approved: 'cyan', processing: 'cyan',
                  paid: 'green', failed: 'red', refunded: 'orange', cancelled: 'gray',
                }[w.status] || 'gray';
                return (
                  <tr key={w._id} className="border-b border-border">
                    <Td><span className="font-mono text-[10px] text-text-muted">{w.reference?.slice(-8)}</span></Td>
                    <Td><span className="font-semibold">{(w.amount || 0).toLocaleString()}</span><span className="text-text-muted text-[10px] ml-1">UGX</span></Td>
                    <Td><span className="text-green font-semibold">{(w.netAmount || 0).toLocaleString()}</span></Td>
                    <Td>{w.method === 'mtn_momo' ? 'MTN' : 'Airtel'}</Td>
                    <Td><span className="font-mono text-xs">{w.phone}</span></Td>
                    <Td><Badge label={w.status?.replace(/_/g, ' ')} color={statusColor} /></Td>
                    <Td>{w.createdAt ? format(new Date(w.createdAt), 'dd/MM/yyyy') : '—'}</Td>
                  </tr>
                );
              })}
            </Table>
          )}
          <div className="mt-4 bg-bg-panel border border-border rounded-xl p-3 text-text-muted text-xs">
            UGX withdrawals are processed via mobile money. Pending approval means your request is in the admin queue.
            Funds are reserved from your balance while pending.
          </div>
        </Card>
      )}

      {/* ── STATEMENT ─────────────────────────────────────────── */}
      {tab === 'statement' && (
        <StatementTab
          fetchAllTransactions={async () => {
            const res = await walletAPI.getTransactions({ limit: 500 });
            return res.transactions || [];
          }}
        />
      )}

      {/* ── EXCHANGE TAB ─────────────────────────────────────── */}
      {tab === 'exchange' && (
        <Card>
          <SectionHeader title="Currency Exchange" />
          <p className="text-text-muted text-sm mb-5">Swap between your DWI wallets at live rates. A 0.5% spread applies.</p>

          {/* Inline exchange form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-1.5">From Wallet</p>
                <select
                  value={exFrom} onChange={e => { setExFrom(e.target.value); computeExPreview(e.target.value, exTo, exAmount); }}
                  className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
                >
                  {['UGX', 'USD', 'EUR', 'USDT'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-1.5">To Wallet</p>
                <select
                  value={exTo} onChange={e => { setExTo(e.target.value); computeExPreview(exFrom, e.target.value, exAmount); }}
                  className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
                >
                  {['UGX', 'USD', 'EUR', 'USDT'].filter(c => c !== exFrom).map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <Input
              label={`Amount (${exFrom})`}
              type="number"
              placeholder="Enter amount to convert"
              value={exAmount}
              onChange={e => { setExAmount(e.target.value); computeExPreview(exFrom, exTo, e.target.value); }}
            />

            {exPreview && (
              <div className="bg-cyan/10 border border-cyan/30 rounded-xl p-3 space-y-1">
                <div className="flex justify-between text-xs text-text-muted">
                  <span>You send</span><span className="font-bold text-text-primary">{parseFloat(exAmount).toLocaleString()} {exFrom}</span>
                </div>
                <div className="flex justify-between text-xs text-text-muted">
                  <span>Exchange fee (0.5%)</span><span>≈ ${exPreview.feeUSD.toFixed(4)} USD</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-text-primary border-t border-cyan/20 pt-1.5 mt-1">
                  <span>You receive</span><span className="text-cyan">{exPreview.amountOut.toLocaleString()} {exTo}</span>
                </div>
              </div>
            )}

            {rates && (
              <div className="bg-bg-panel border border-border rounded-xl p-3">
                <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2">Live Rates</p>
                <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
                  <span>1 USD = {(rates.USD_TO_UGX || 3700).toLocaleString()} UGX</span>
                  <span>·</span>
                  <span>1 EUR = {(rates.EUR_TO_UGX || 4000).toLocaleString()} UGX</span>
                  <span>·</span>
                  <span>1 USDT ≈ $1.00</span>
                </div>
              </div>
            )}

            <Button
              className="w-full" size="lg" loading={exLoading}
              disabled={!exAmount || parseFloat(exAmount) <= 0 || exFrom === exTo || !exPreview}
              onClick={handleExchange}
            >
              <ArrowLeftRight className="w-4 h-4" /> Exchange Now
            </Button>
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════
          DEPOSIT MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal
        open={depositModal}
        onClose={() => { setDepositModal(false); setDepAmount(''); setDepPhone(''); }}
        title="Deposit Funds"
        size="md"
      >
        <div className="space-y-5">
          {/* Currency selector */}
          <div>
            <p className="text-text-secondary text-xs font-semibold mb-2 uppercase tracking-wider">Deposit Currency</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DEP_CURRENCIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setDepCurrency(c.id)}
                  className={clsx(
                    'flex flex-col items-center gap-1 p-3 rounded-xl border transition-all text-center',
                    depCurrency === c.id
                      ? 'border-cyan bg-cyan/10 text-cyan'
                      : 'border-border bg-bg-panel text-text-secondary hover:border-border-hi'
                  )}
                >
                  <span className="text-lg leading-none">{c.flag}</span>
                  <span className="text-xs font-bold">{c.label}</span>
                  <span className="text-[9px] text-text-muted leading-none">{c.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <Input
            label={`Amount (${depCurrency})`}
            type="number"
            placeholder={DEP_PLACEHOLDER[depCurrency]}
            value={depAmount}
            onChange={e => setDepAmount(e.target.value)}
            note={`Minimum: ${DEP_MIN[depCurrency]} ${depCurrency}`}
          />

          {/* Estimated credit */}
          {est && (
            <div className="flex items-center justify-between bg-green/10 border border-green/25 rounded-xl px-4 py-3">
              <div>
                <p className="text-text-muted text-[10px] uppercase tracking-wider font-semibold">Estimated Credit</p>
                <p className="text-green font-black text-base mt-0.5">{est.credit}</p>
                {est.ugxNote && <p className="text-text-muted text-xs mt-0.5">{est.ugxNote}</p>}
              </div>
              <div className="text-right">
                <p className="text-text-muted text-[10px] uppercase tracking-wider font-semibold">To Wallet</p>
                <p className="text-text-primary font-bold text-sm mt-0.5">{est.wallet}</p>
              </div>
            </div>
          )}

          {/* Payment method — only for mobile money currencies */}
          {(depCurrency === 'UGX' || depCurrency === 'USD' || depCurrency === 'EUR') && (
            <>
              <div>
                <p className="text-text-secondary text-xs font-semibold mb-2 uppercase tracking-wider">Payment Method</p>
                <div className="grid grid-cols-2 gap-2">
                  {DEPOSIT_METHODS.filter(m => ['mtn_momo', 'airtel_money'].includes(m.id)).map(m => (
                    <button
                      key={m.id} onClick={() => setDepMethod(m.id)}
                      className={clsx(
                        'flex items-center gap-2 p-3 rounded-xl border transition-all',
                        depMethod === m.id ? 'border-cyan bg-cyan/10 text-cyan' : 'border-border bg-bg-panel text-text-secondary hover:border-border-hi'
                      )}
                    >
                      <span className="text-lg">{m.icon}</span>
                      <span className="text-xs font-semibold">{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Input
                label="Phone Number"
                type="tel"
                placeholder="+256 77X XXX XXX"
                value={depPhone}
                onChange={e => setDepPhone(e.target.value)}
              />
            </>
          )}

          {/* USDT instructions */}
          {depCurrency === 'USDT' && (
            <div className="bg-bg-panel border border-border rounded-xl p-4 space-y-2">
              <p className="text-text-muted text-xs font-semibold uppercase tracking-wider">Send USDT to</p>
              <div className="flex items-center gap-2 bg-bg rounded-lg px-3 py-2">
                <code className="text-cyan text-xs flex-1 break-all font-mono">
                  {process.env.REACT_APP_USDT_ADDRESS || 'Contact support for USDT address'}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText('USDT_ADDRESS').then(() => toast.success('Copied!'))}
                  className="text-text-muted hover:text-cyan flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-text-muted text-xs">Network: TRC-20 (Tron). Minimum 1 USDT.</p>
            </div>
          )}

          <Button className="w-full" size="lg" loading={depLoading} onClick={handleDeposit}>
            {depCurrency === 'USDT' ? 'I\'ve Sent USDT' : 'Send Deposit Request'}
          </Button>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          WITHDRAW MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal
        open={withdrawModal}
        onClose={() => { setWithdrawModal(false); setWdCurrency('UGX'); setWdAmount(''); setWdPhone(''); setWdStep(1); setWdConfirmOpen(false); setWdConfirmed(false); setWdQuote(null); }}
        title={wdStep === 1 ? 'Withdraw Funds' : `Withdraw ${wdCurrency}`}
        size="md"
      >
        <div className="space-y-4">

          {/* ── Step 1: pick wallet ───────────────────────────────── */}
          {wdStep === 1 && (
            <div className="space-y-3">
              <p className="text-text-muted text-sm">Choose which wallet to withdraw from.</p>
              <div className="grid grid-cols-2 gap-3">
                {WD_CURRENCIES.map(c => {
                  const bal = c.id === 'UGX' ? (w.ugxBalance || 0)
                            : c.id === 'USD' ? (w.balance    || 0)
                            : c.id === 'EUR' ? (w.eurBalance  || 0)
                            : (w.usdtBalance || 0);
                  const fmt = c.id === 'UGX'  ? fmtUGX(bal)
                            : c.id === 'USD'  ? fmtUSD(bal)
                            : c.id === 'EUR'  ? fmtEUR(bal)
                            : fmtUSDT(bal);
                  const color = c.id === 'UGX' ? '#FFD700' : c.id === 'USD' ? '#06C8E8' : c.id === 'EUR' ? '#3B82F6' : '#26A17B';
                  return (
                    <button
                      key={c.id}
                      onClick={() => { setWdCurrency(c.id); setWdStep(2); }}
                      className="flex flex-col items-start gap-1.5 p-3 rounded-xl border border-border bg-bg-panel hover:border-cyan hover:bg-cyan/5 transition-all text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg leading-none">{c.flag}</span>
                        <span className="text-text-primary text-sm font-bold">{c.id}</span>
                      </div>
                      <p className="font-black text-base leading-tight" style={{ color }}>{fmt}</p>
                      <p className="text-text-muted" style={{ fontSize: 10 }}>
                        {c.id === 'USDT' ? 'Via Exchange' : c.id === 'EUR' ? 'Bank transfer' : c.label.split('—')[1]?.trim()}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 2: enter details ─────────────────────────────── */}
          {wdStep === 2 && (
            <div className="space-y-4">
              {/* Back link */}
              <button
                onClick={() => { setWdStep(1); setWdAmount(''); setWdPhone(''); }}
                className="text-cyan text-xs font-semibold hover:underline flex items-center gap-1"
              >
                ← Choose different wallet
              </button>

              {/* Balance display */}
              <div className="flex items-center justify-between px-1 pb-2 border-b border-border">
                <span className="text-text-muted text-xs font-semibold uppercase tracking-wider">{wdCurrency} Balance</span>
                <span className="text-cyan font-black text-xl">{fmtWdBalance(wdAvailable)}</span>
              </div>

              {/* Mobile money method selector (UGX and USD only) */}
              {wdIsMoMo && (
                <div className="grid grid-cols-2 gap-2">
                  {WITHDRAW_METHODS.map(m => (
                    <button
                      key={m.id} onClick={() => setWdMethod(m.id)}
                      className={clsx(
                        'flex items-center gap-2 p-3 rounded-xl border transition-all',
                        wdMethod === m.id ? 'border-cyan bg-cyan/10 text-cyan' : 'border-border bg-bg-panel text-text-secondary hover:border-border-hi'
                      )}
                    >
                      <span className="text-lg">{m.icon}</span>
                      <span className="text-xs font-semibold">{m.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* EUR/USDT — no mobile money backend yet */}
              {!wdIsMoMo && (
                <div className="bg-blue/10 border border-blue/25 rounded-xl p-3 text-blue text-xs">
                  {wdCurrency === 'USDT'
                    ? 'ℹ USDT withdrawals are processed via blockchain. Use the Crypto section to withdraw USDT.'
                    : 'ℹ EUR withdrawals are processed via international bank transfer. Contact support@dwifintech.com to initiate.'}
                </div>
              )}

              {/* Phone number (mobile money only) */}
              {wdIsMoMo && (
                <Input
                  label="Recipient Phone Number"
                  type="tel"
                  placeholder="+256 77X XXX XXX"
                  value={wdPhone}
                  onChange={e => {
                    const ph = e.target.value;
                    setWdPhone(ph);
                    // Auto-detect MTN / Airtel from Uganda prefix
                    const norm = ph.replace(/[\s\-]/g, '').replace(/^0/, '256').replace(/^\+/, '');
                    const pfx5 = norm.slice(0, 5);
                    if (['25676','25677','25678','25679'].includes(pfx5)) setWdMethod('mtn_momo');
                    else if (['25670','25674','25675'].includes(pfx5))    setWdMethod('airtel_money');
                  }}
                />
              )}

              {/* Amount input */}
              {wdIsMoMo && (
                <Input
                  label={`Amount (${wdCurrency})`}
                  type="number"
                  placeholder={wdCurrency === 'UGX' ? 'e.g. 50,000' : 'e.g. 100.00'}
                  value={wdAmount}
                  onChange={e => setWdAmount(e.target.value)}
                  note={`Available: ${fmtWdBalance(wdAvailable)}`}
                />
              )}

              {/* KYC notice for large withdrawals */}
              {wdShowKyc && (
                <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 text-amber text-xs">
                  ⚠ KYC verification required for {wdCurrency} withdrawals above {wdKycThr?.toLocaleString()} {wdCurrency}. Please verify your identity to proceed.
                </div>
              )}

              {/* Fee summary — figures from the backend quote endpoint (no funds moved).
                  wdQuote.breakdown (Service fee + Network cost line items) is only ever
                  populated when WITHDRAWAL_FEE_ENGINE_V2 is on; otherwise this renders
                  the same single "Fee" line it always has. */}
              {wdIsMoMo && wdAmt >= wdMinAmt && (
                <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1">
                  <div className="flex items-center justify-between text-xs text-text-muted">
                    <span>Gross amount</span>
                    <span>{fmtWdBalance(wdQuote ? wdQuote.gross : wdAmt)}</span>
                  </div>
                  {wdQuote?.breakdown ? (
                    wdQuote.breakdown.map((line) => (
                      <div key={line.code} className="flex items-center justify-between text-xs text-text-muted">
                        <span>{line.label}</span>
                        <span>− {fmtWdBalance(line.amount)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-between text-xs text-text-muted">
                      <span>Fee{wdQuoteLoading ? ' …' : ''}</span>
                      <span>{wdQuote ? `− ${fmtWdBalance(wdQuote.fee)}` : '—'}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs font-bold text-text-primary border-t border-border pt-1 mt-1">
                    <span>You receive</span>
                    <span className="text-green">{wdQuote ? fmtWdBalance(wdQuote.net) : '—'}</span>
                  </div>
                </div>
              )}

              <div className="bg-bg-panel border border-border rounded-xl p-3 text-text-muted text-xs">
                ℹ Withdrawals are reviewed within 1–24 hours. Funds remain reserved until processed.
              </div>

              <Button
                variant="outline" className="w-full" size="lg"
                loading={wdQuoteLoading}
                disabled={!wdIsMoMo || !wdAmount || wdAmt < wdMinAmt || wdShowKyc || !wdPhone}
                onClick={handleWithdrawReview}
              >
                Review Withdrawal →
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          WITHDRAW CONFIRMATION MODAL  (display/review only —
          "Confirm & Submit" calls the same handleWithdraw() as before)
      ══════════════════════════════════════════════════════════ */}
      <Modal
        open={wdConfirmOpen}
        onClose={() => { if (!wdLoading) setWdConfirmOpen(false); }}
        title="Confirm Withdrawal"
        size="sm"
      >
        {wdQuote && (
          <div className="space-y-4">
            {/* Destination — emphasised */}
            <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1">
              <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold">Sending to</p>
              <p className="text-text-primary text-2xl font-black font-mono tracking-wide break-all">{wdPhone}</p>
              <p className="text-text-muted text-[11px]">
                {WITHDRAW_METHODS.find(m => m.id === wdMethod)?.icon}{' '}
                {WITHDRAW_METHODS.find(m => m.id === wdMethod)?.label || wdMethod} · {wdCurrency}
              </p>
            </div>

            {/* Money summary — wdQuote.breakdown (Service fee + Network cost) only
                appears when WITHDRAWAL_FEE_ENGINE_V2 is on; otherwise this is the
                same single "Fee" line as before. */}
            <div className="bg-bg-elevated border border-border rounded-xl divide-y divide-border/40">
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-text-muted">Gross amount</span>
                <span className="font-semibold">{fmtWdBalance(wdQuote.gross)}</span>
              </div>
              {wdQuote.breakdown ? (
                wdQuote.breakdown.map((line) => (
                  <div key={line.code} className="px-4 py-3 flex justify-between text-sm">
                    <span className="text-text-muted">{line.label}</span>
                    <span className="text-amber font-semibold">− {fmtWdBalance(line.amount)}</span>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 flex justify-between text-sm">
                  <span className="text-text-muted">Fee</span>
                  <span className="text-amber font-semibold">− {fmtWdBalance(wdQuote.fee)}</span>
                </div>
              )}
              <div className="px-4 py-3 flex justify-between">
                <span className="text-text-primary font-bold">You receive</span>
                <span className="text-green font-black text-xl">{fmtWdBalance(wdQuote.net)}</span>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 bg-red/5 border border-red/20 rounded-xl p-3">
              <span className="text-red text-sm leading-none mt-0.5">⚠</span>
              <p className="text-red text-xs leading-relaxed">
                <strong>Check the number.</strong> Money sent to the wrong mobile money number cannot be recovered.
              </p>
            </div>

            {/* Required confirmation */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={wdConfirmed}
                onChange={e => setWdConfirmed(e.target.checked)}
                className="accent-cyan w-4 h-4 mt-0.5"
              />
              <span className="text-xs text-text-secondary leading-relaxed">
                I confirm <span className="text-text-primary font-mono font-bold">{wdPhone}</span> is the correct destination number.
              </span>
            </label>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setWdConfirmOpen(false)} disabled={wdLoading}>
                ← Back / Edit
              </Button>
              <Button
                className="flex-1"
                loading={wdLoading}
                disabled={!wdConfirmed || wdLoading}
                onClick={handleWithdraw}
              >
                <Send className="w-4 h-4" />
                Confirm &amp; Submit
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          EXCHANGE MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal
        open={exchangeModal}
        onClose={() => { setExchangeModal(false); setExAmount(''); setExPreview(null); }}
        title="Exchange Currency"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-text-muted text-sm">Convert between your DWI wallets instantly. 0.5% spread applies.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-1.5">From</p>
              <select
                value={exFrom} onChange={e => { setExFrom(e.target.value); computeExPreview(e.target.value, exTo, exAmount); }}
                className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
              >
                {['UGX', 'USD', 'EUR', 'USDT'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-1.5">To</p>
              <select
                value={exTo} onChange={e => { setExTo(e.target.value); computeExPreview(exFrom, e.target.value, exAmount); }}
                className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2.5 text-text-primary text-sm outline-none focus:border-cyan"
              >
                {['UGX', 'USD', 'EUR', 'USDT'].filter(c => c !== exFrom).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <Input
            label={`Amount (${exFrom})`}
            type="number"
            placeholder="Amount to convert"
            value={exAmount}
            onChange={e => { setExAmount(e.target.value); computeExPreview(exFrom, exTo, e.target.value); }}
          />
          {exPreview && (
            <div className="bg-cyan/10 border border-cyan/30 rounded-xl p-3 space-y-1">
              <div className="flex justify-between text-xs text-text-muted">
                <span>You send</span><span className="font-bold text-text-primary">{parseFloat(exAmount || 0).toLocaleString()} {exFrom}</span>
              </div>
              <div className="flex justify-between text-xs text-text-muted">
                <span>Fee (0.5%)</span><span>≈ ${(exPreview.feeUSD || 0).toFixed(4)} USD</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-text-primary border-t border-cyan/20 pt-1.5">
                <span>You receive</span><span className="text-cyan">{(exPreview.amountOut || 0).toLocaleString()} {exTo}</span>
              </div>
            </div>
          )}
          <Button
            className="w-full" size="lg" loading={exLoading}
            disabled={!exAmount || parseFloat(exAmount) <= 0 || exFrom === exTo || !exPreview}
            onClick={handleExchange}
          >
            <ArrowLeftRight className="w-4 h-4" /> Confirm Exchange
          </Button>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          SEND / TRANSFER MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal
        open={transferModal}
        onClose={() => { setTransferModal(false); setTfRecipient(''); setTfAmount(''); setTfNote(''); setTfCurrency('UGX'); }}
        title="Send to DWI User"
        size="sm"
      >
        {(() => {
          const tfAvail = tfCurrency === 'UGX' ? (w.ugxBalance || 0) : tfCurrency === 'USD' ? (w.balance || 0) : tfCurrency === 'EUR' ? (w.eurBalance || 0) : (w.usdtBalance || 0);
          const fmtTfBal = tfCurrency === 'UGX' ? fmtUGX(tfAvail) : tfCurrency === 'EUR' ? fmtEUR(tfAvail) : tfCurrency === 'USDT' ? fmtUSDT(tfAvail) : fmtUSD(tfAvail);
          const tfPlaceholder = tfCurrency === 'UGX' ? 'e.g. 5,000' : 'e.g. 1.00';
          return (
            <div className="space-y-4">
              <p className="text-text-muted text-sm">Send funds instantly to another DWI Fintech member by their email or phone number.</p>
              <Input
                label="Recipient (Email or Phone)"
                type="text"
                placeholder="you@example.com or +256 77X XXX XXX"
                value={tfRecipient}
                onChange={e => setTfRecipient(e.target.value)}
              />
              <div>
                <p className="text-text-secondary text-xs font-medium mb-1.5">Wallet</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { id: 'UGX',  flag: '🇺🇬', bal: fmtUGX(w.ugxBalance  || 0) },
                    { id: 'USD',  flag: '🇺🇸', bal: fmtUSD(w.balance      || 0) },
                    { id: 'EUR',  flag: '🇪🇺', bal: fmtEUR(w.eurBalance  || 0) },
                    { id: 'USDT', flag: '₮',  bal: fmtUSDT(w.usdtBalance || 0) },
                  ].map(c => (
                    <button
                      key={c.id}
                      onClick={() => { setTfCurrency(c.id); setTfAmount(''); }}
                      className={`flex flex-col items-center py-2 px-1 rounded-xl border text-center transition-all ${
                        tfCurrency === c.id
                          ? 'border-cyan bg-cyan/10 text-cyan'
                          : 'border-border bg-bg-panel text-text-muted hover:border-cyan/40'
                      }`}
                    >
                      <span className="text-base leading-none mb-0.5">{c.flag}</span>
                      <span className="text-[10px] font-bold">{c.id}</span>
                      <span className="text-[9px] text-text-muted leading-tight">{c.bal}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Input
                label={`Amount (${tfCurrency})`}
                type="number"
                placeholder={tfPlaceholder}
                value={tfAmount}
                onChange={e => setTfAmount(e.target.value)}
                note={`Available: ${fmtTfBal}`}
              />
              <Input
                label="Note (optional)"
                type="text"
                placeholder="What's this transfer for?"
                value={tfNote}
                onChange={e => setTfNote(e.target.value)}
              />
              <Button
                className="w-full" loading={tfLoading}
                disabled={!tfRecipient || !tfAmount || parseFloat(tfAmount) <= 0}
                onClick={handleTransfer}
              >
                <Send className="w-4 h-4" /> Send Funds
              </Button>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
